import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, getApiErrorMessage, setRequestContextGetter } from '@/lib/http';
import { posthog } from '@/lib/posthog';
import { clearAccessToken, getAccessToken, setAccessToken } from '@/lib/session';
import { resetOrgScopedStores } from '@/store/orgScopedReset';
import type { AuthContextValue, AuthState, LoginOption, V2MeResponse } from './auth-types';

const initialState: AuthState = {
  user: null,
  organizations: [],
  organization: null,
  workspaces: [],
  currentWorkspaceId: null,
  memberships: [],
  permissions: [],
  loading: true,
  ready: false,
  error: null,
};

const AuthContext = createContext<AuthContextValue | null>(null);

function normalizeMe(data: V2MeResponse): AuthState {
  return {
    user: data.user,
    organizations: data.organizations || [],
    organization: data.organization || null,
    workspaces: data.workspaces || [],
    currentWorkspaceId: data.currentWorkspaceId || null,
    memberships: data.memberships || [],
    permissions: data.permissions || [],
    loading: false,
    ready: true,
    error: null,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);

  useEffect(() => {
    setRequestContextGetter(() => ({
      organizationId: state.organization?._id || null,
      workspaceId: state.currentWorkspaceId,
    }));
  }, [state.organization?._id, state.currentWorkspaceId]);

  // Org switch happens WITHOUT a page reload (switchOrg → init()), so feature
  // stores that cache org-scoped data (outreach accounts/campaigns, badge
  // counts, …) must be told to drop it — otherwise the UI keeps rendering the
  // PREVIOUS organization's data. Fires only on a real change of org id, not on
  // the initial login resolve.
  const prevOrgIdRef = useRef<string | null>(null);
  useEffect(() => {
    const orgId = state.organization?._id || null;
    if (prevOrgIdRef.current && orgId && prevOrgIdRef.current !== orgId) {
      resetOrgScopedStores();
    }
    if (orgId) prevOrgIdRef.current = orgId;
  }, [state.organization?._id]);

  useEffect(() => {
    if (!state.user) return;
    const fullName = [state.user.firstName, state.user.lastName].filter(Boolean).join(' ').trim();
    const orgId = state.organization?._id;
    const role = state.memberships.find((m) => m.organizationId === orgId)?.roleName || '';

    posthog.identify(state.user.email, {
      email: state.user.email,
      organizationId: orgId || '',
      organizationName: state.organization?.name || '',
      role,
      fullName,
    });
  }, [state.user, state.organization, state.memberships]);

  const init = useCallback(async () => {
    const token = getAccessToken();

    if (!token) {
      setState({ ...initialState, loading: false, ready: true });
      return false;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const { data } = await api.get<V2MeResponse>('/v2/iam/me');
      setState(normalizeMe(data));
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error, 'Unable to load your session.');
      clearAccessToken();
      setState({ ...initialState, loading: false, ready: true, error: message });
      return false;
    }
  }, []);

  useEffect(() => {
    void init();
  }, [init]);

  const resolveLoginOptions = useCallback(async (email: string) => {
    const { data } = await api.post<{ options?: LoginOption[]; mode?: string; ssoToken?: string }>(
      '/auth/resolve-otp',
      { email },
    );

    if (Array.isArray(data.options) && data.options.length > 0) {
      return data.options;
    }

    if (data.mode === 'sso' && data.ssoToken) {
      return [
        {
          kind: 'sso',
          organizationId: 'default',
          organizationName: '',
          ssoToken: data.ssoToken,
        },
      ] satisfies LoginOption[];
    }

    return [{ kind: 'otp' }] satisfies LoginOption[];
  }, []);

  const sendOtp = useCallback(async (email: string, inspect = false) => {
    await api.post('/auth/send-otp', { email, ...(inspect ? { inspect: true } : {}) });
  }, []);

  const verifyOtp = useCallback(
    async (email: string, otp: string) => {
      const { data } = await api.post<{ accessToken: string }>('/auth/verify-otp', { email, otp });
      setAccessToken(data.accessToken);
      const ok = await init();
      if (!ok) throw new Error('Login succeeded, but the app could not load your workspace.');
    },
    [init],
  );

  const logout = useCallback(async () => {
    let idpLogoutUrl: string | null = null;

    try {
      const { data } = await api.post<{ idpLogoutUrl?: string }>('/auth/logout');
      idpLogoutUrl = data.idpLogoutUrl || null;
    } catch (error) {
      console.error('[auth] server logout failed:', error);
    } finally {
      clearAccessToken();
      posthog.reset();
      setState({ ...initialState, loading: false, ready: true });
    }

    return idpLogoutUrl;
  }, []);

  const switchWorkspace = useCallback(async (workspaceId: string) => {
    await api.post('/v2/iam/switch-workspace', { workspaceId });
    setState((prev) => ({ ...prev, currentWorkspaceId: workspaceId }));
  }, []);

  const switchOrg = useCallback(
    async (organizationId: string) => {
      await api.post('/v2/iam/switch-org', { organizationId });
      await init();
    },
    [init],
  );

  const createWorkspace = useCallback(
    async (name: string) => {
      await api.post('/v2/iam/workspace', { name });
      await init();
    },
    [init],
  );

  const hasPermission = useCallback(
    (permission: string) => state.permissions.includes(permission),
    [state.permissions],
  );

  const hasAnyPermission = useCallback(
    (...permissions: string[]) => permissions.some((permission) => state.permissions.includes(permission)),
    [state.permissions],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      init,
      resolveLoginOptions,
      sendOtp,
      verifyOtp,
      logout,
      switchWorkspace,
      switchOrg,
      createWorkspace,
      hasPermission,
      hasAnyPermission,
    }),
    [
      state,
      init,
      resolveLoginOptions,
      sendOtp,
      verifyOtp,
      logout,
      switchWorkspace,
      switchOrg,
      createWorkspace,
      hasPermission,
      hasAnyPermission,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider.');
  }

  return context;
}
